import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { ProjectFileResponseDto } from '@inference.engine/workspace-api-client/web';
import { Dialog, DialogContent, DialogTitle } from '@monksflow/monks-ui';
import { createPortal } from 'react-dom';
import { useAnnotationFullscreen } from '@/features/comment-on-file';
import type { AssetFile } from '../../assetDetail.types';
import { FullscreenTopBar } from './FullscreenTopBar';
import { useTranslation } from 'react-i18next';

interface Props {
  /** Preview subtree (image / video / pdf viewer). */
  children: ReactNode;
  /** Current asset; required to render the top bar's controls and download action. */
  file: AssetFile | null;
  /** Sibling files in the current folder, for prev/next navigation. */
  siblingFiles: ProjectFileResponseDto[];
  projectId: string;
  folderId: string;
}

/**
 * Wraps the asset preview so the user can toggle a full-viewport view.
 * The preview subtree is portalled into a stable detached node whose DOM
 * parent is reparented imperatively when `isFullscreen` flips — children
 * stay mounted, so refs (img, svg, video) and any in-flight pointer-driven
 * draft survive the toggle.
 *
 * The fullscreen chrome is a Monks UI Dialog (`size="fullscreen"`) so we
 * inherit a proper translucent backdrop, focus trap, scroll lock, and
 * Escape handling. The dialog's close animation gives the layout-effect
 * enough time to reparent the portal node back to the inline host before
 * Radix unmounts the content.
 */
export function AssetDetailFullScreen({
  children,
  file,
  siblingFiles,
  projectId,
  folderId,
}: Props) {
  const { t } = useTranslation('projects');

  const { isFullscreen, setFullscreen } = useAnnotationFullscreen();
  const inlineHostRef = useRef<HTMLDivElement>(null);
  // Callback ref instead of useRef so the layout-effect re-runs when Radix's
  // DialogPortal actually mounts the host — `useRef` wouldn't notify React,
  // and the effect would otherwise see `null` on the first fullscreen render
  // and never retry, leaving the preview stranded in the inline host.
  const [fullscreenHost, setFullscreenHost] = useState<HTMLDivElement | null>(null);

  // Stable portal target. `display: contents` so it doesn't insert an extra
  // layout box between the host and the preview/toolbar.
  const portalNode = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- standard DOM API
    const node = document.createElement<'div'>('div');
    node.style.display = 'contents';
    return node;
  }, []);

  useLayoutEffect(() => {
    const host = isFullscreen ? fullscreenHost : inlineHostRef.current;
    if (!host) return;
    host.appendChild(portalNode);
  }, [isFullscreen, portalNode, fullscreenHost]);

  useEffect(() => {
    return () => {
      portalNode.remove();
    };
  }, [portalNode]);

  return (
    <>
      <div ref={inlineHostRef} className="contents" />
      <Dialog
        open={isFullscreen}
        onOpenChange={(next) => {
          if (!next) setFullscreen(false);
        }}
      >
        <DialogContent
          size="fullscreen"
          showCloseButton={false}
          className="flex flex-col gap-0 bg-background/20 p-0 backdrop-blur-lg"
          aria-describedby={undefined}
        >
          {/* Radix requires a DialogTitle for screen readers; the fullscreen
              viewer has no visible heading, so hide it with `sr-only`. */}
          <DialogTitle className="sr-only">
            {t('projectDetail.assetDetailFullScreen.assetPreview')}
          </DialogTitle>
          {file && (
            <FullscreenTopBar
              file={file}
              siblingFiles={siblingFiles}
              projectId={projectId}
              folderId={folderId}
              onClose={() => setFullscreen(false)}
            />
          )}
          <div className="flex min-h-0 flex-1 flex-col px-6 pb-6">
            <div
              ref={setFullscreenHost}
              className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg"
            />
          </div>
        </DialogContent>
      </Dialog>
      {createPortal(children, portalNode)}
    </>
  );
}
